import React from "react";
import * as Select from "@radix-ui/react-select";
import { Control, FieldPath, FieldValues, useController, useFormContext } from "react-hook-form";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

interface SelectItemProps {
  children: React.ReactNode;
  value: string;
  disabled?: boolean;
}

interface CustomSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  options: Array<{ label: string; value: string; disabled?: boolean }>;
  defaultValue?: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
  name: TName;
  control?: Control<TFieldValues>;
  disabled?: boolean;
  required?: boolean;
}

export const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, value, disabled, ...props }, forwardedRef) => (
    <Select.Item
      value={value}
      disabled={disabled}
      {...props}
      ref={forwardedRef}
      className="relative flex cursor-pointer select-none items-center rounded-md px-8 py-2 text-sm outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-violet-100 data-[disabled]:text-gray-400 data-[highlighted]:text-violet-900"
    >
      <Select.ItemText>{children}</Select.ItemText>
      <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
        <Check />
      </Select.ItemIndicator>
    </Select.Item>
  ),
);

SelectItem.displayName = "SelectItem";

interface CustomSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  options: Array<{ label: string; value: string; disabled?: boolean }>;
  defaultValue?: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
  name: TName;
  disabled?: boolean;
  required?: boolean;
}

export function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  placeholder = "Select an option...",
  name,
  disabled = false,
  required = false,
}: CustomSelectProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div className="space-y-1">
      <Select.Root
        value={field.value}
        onValueChange={field.onChange}
        name={field.name}
        disabled={disabled}
        required={required}
      >
        <Select.Trigger
          className={`inline-flex items-center justify-between gap-2 rounded border bg-white px-4 py-2 text-sm ${
            error ? "border-red-500" : "border-gray-300"
          } outline-none hover:bg-gray-50 focus:ring-2 data-[placeholder]:text-gray-400 ${
            error ? "focus:ring-red-500" : "focus:ring-violet-500"
          } w-full focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50`}
          aria-label={placeholder}
          ref={field.ref}
        >
          <Select.Value placeholder={placeholder} />
          <Select.Icon>
            <ChevronDown />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
          >
            <Select.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-white text-violet-700">
              <ChevronUp />
            </Select.ScrollUpButton>

            <Select.Viewport className="p-2">
              {options.map(option => (
                <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </SelectItem>
              ))}
            </Select.Viewport>

            <Select.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-white text-violet-700">
              <ChevronDown />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );
}
